import { useEffect, useState } from 'react';
import { useColorMode } from 'features/theme/useColorMode';
import { applyBrandForPath, applyLoadedBrand } from './applyBrand';
import { getPointedBrand, readCachedBrand } from './brandCache';
import type { BrandPointer } from '@hub/common';

// Applies the brand file a participation's manifest points at while the
// calling component is mounted. `pointer` is undefined while the
// participation loads (the cached brand of `protocolId` applies) and null for
// a manifest without a brand. The page counts as branded until the manifest
// names no brand: light and without the dark mode switch while the
// participation or the file loads and after the file fails to load. On
// unmount the brand of the current address applies. Returns whether the brand
// is settled: no protocol or no brand named, or its file applied or failed to
// load.
export const useParticipationBrand = (
  protocolId: string | undefined,
  pointer: BrandPointer | null | undefined
): boolean => {
  const { setActiveBrand } = useColorMode();
  const brandId = pointer?.id;
  const brandUrl = pointer === undefined ? undefined : (pointer?.url ?? null);
  const [settledUrl, setSettledUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (protocolId === undefined) return;
    const controller = new AbortController();
    if (brandUrl === undefined) {
      setActiveBrand(readCachedBrand(protocolId)?.file ?? 'requested');
    } else {
      const target =
        brandUrl === null || brandId === undefined
          ? null
          : { id: brandId, url: brandUrl };
      if (target) setActiveBrand('requested');
      void applyLoadedBrand(
        getPointedBrand(protocolId, target, controller.signal),
        controller.signal,
        setActiveBrand,
        { protocolId, ...(target && { url: target.url }) }
      ).then(() => {
        if (target && !controller.signal.aborted) setSettledUrl(target.url);
      });
    }
    return () => {
      controller.abort();
      applyBrandForPath(setActiveBrand);
    };
  }, [protocolId, brandId, brandUrl, setActiveBrand]);

  return (
    protocolId === undefined ||
    typeof brandUrl !== 'string' ||
    settledUrl === brandUrl
  );
};
